/**
 * CandleHeadGear — the `candle` head family: a lit candle worn on the crown.
 *
 * A reusable head template (first item: Mallow's Rare Wick). The descriptor's
 * `primaryColor` is the wax and its `accentColor` is the flame, so later items
 * re-colour it without new art (a blue-flame stub, a black-wax taper). The
 * whole ghost bakes into one static SVG texture, so the flame is static.
 *
 * Variants (the authored `variant` integer; new shapes append, never reorder):
 *   0 `taper` — one tall slim candle standing in a brass dish.
 *   1 `stub`  — a short fat candle in a pool of its own wax, one drip over the lip.
 * Any other value draws `taper`.
 *
 * This lives outside GearRecipes/GearSilhouettes: both sit at their file-size
 * ceilings, and the candle shares no geometry with the stroke-shell head
 * families (hood/helm/hat/...). GearSilhouettes registers the renderer.
 */
import type { BodyRig } from './HumanoidGeometry';
import {
  bodyRigForGear,
  darken,
  GEAR_COLORS,
  normalizeGearVariant,
  type GearFitContext,
} from './GearGeometry';

export const CANDLE_VARIANTS = ['taper', 'stub'] as const;
export type CandleVariant = (typeof CANDLE_VARIANTS)[number];

/** Flame colour when a descriptor authors no accent: warm lantern amber. */
export const CANDLE_DEFAULT_FLAME = '#f4b04c';

/**
 * Highest y any candle part reaches. The large body scales 1.04x about
 * y = 60, so 3.4 still lands inside the 140x120 viewBox.
 */
export const CANDLE_CEILING_Y = 3.4;

const HOLDER = { metal: '#c09447', shade: '#7f5e2a', shine: '#f3dc9c' } as const;
const WICK_COLOR = '#3a2a1f';
const NON_SCALING_STROKE = 'vector-effect="non-scaling-stroke"';

interface CandleProfile {
  halfWidth: number;
  height: number;
  dipDepth: number;
  dishRadius: number;
  flameHalfWidth: number;
  flameHeight: number;
}

const PROFILES: Record<CandleVariant, CandleProfile> = {
  taper: { halfWidth: 5.2, height: 17, dipDepth: 1.4, dishRadius: 12.5, flameHalfWidth: 4.3, flameHeight: 10.4 },
  stub: { halfWidth: 8.2, height: 8.5, dipDepth: 1.9, dishRadius: 14, flameHalfWidth: 4.8, flameHeight: 11 },
};

export interface CandleRecipeGeometry {
  variant: CandleVariant;
  variantTier: number;
  centerX: number;
  dishY: number;
  waxTopY: number;
  flameTipY: number;
  dishBack: string;
  dishFront: string;
  dishShine: string;
  pool: string;
  wax: string;
  waxRim: string;
  waxHighlight: string;
  waxShade: string;
  drip: string;
  flame: string;
  flameCore: string;
  wick: string;
  glow: { cx: number; cy: number; rx: number; ry: number };
}

function n(value: number): string {
  return String(Number(value.toFixed(2)));
}

/** Variant integer → named candle shape (unknown values draw `taper`). */
export function candleVariant(variant: number): CandleVariant {
  return CANDLE_VARIANTS[normalizeGearVariant(variant)] ?? 'taper';
}

/** A bottom-anchored teardrop whose tip leans slightly back (away from the face). */
function teardrop(x: number, bottomY: number, halfWidth: number, height: number, lean: number): string {
  const tipX = x - lean;
  const tipY = bottomY - height;
  return [
    `M${n(tipX)},${n(tipY)}`,
    `C${n(tipX + 0.9)},${n(tipY + height * 0.32)} ${n(x + halfWidth)},${n(bottomY - height * 0.5)} ${n(x + halfWidth)},${n(bottomY - height * 0.22)}`,
    `C${n(x + halfWidth)},${n(bottomY - 0.6)} ${n(x + halfWidth * 0.45)},${n(bottomY)} ${n(x)},${n(bottomY)}`,
    `C${n(x - halfWidth * 0.45)},${n(bottomY)} ${n(x - halfWidth)},${n(bottomY - 0.6)} ${n(x - halfWidth)},${n(bottomY - height * 0.22)}`,
    `C${n(x - halfWidth)},${n(bottomY - height * 0.5)} ${n(tipX - 0.9)},${n(tipY + height * 0.32)} ${n(tipX)},${n(tipY)} Z`,
  ].join(' ');
}

export function candleRecipe(rig: BodyRig, variant: number): CandleRecipeGeometry {
  const shape = candleVariant(variant);
  const p = PROFILES[shape];
  const h = rig.head;
  const cx = (h.openingLeft + h.openingRight) / 2;
  // The dish sits on the upper forehead, above the eyes and below the dome top.
  const dishY = h.openingTopY + 4;
  const dr = p.dishRadius;
  const hw = p.halfWidth;
  // The flame's base dips 0.3 into the melted top; its glow reaches 0.6 above the tip.
  const waxTopY = Math.max(dishY - p.height, CANDLE_CEILING_Y + 0.6 + p.flameHeight - 0.3);
  const t = waxTopY;
  const x0 = cx - hw;
  const x1 = cx + hw;
  const dip = hw * 0.5;

  const wax = [
    `M${n(x0)},${n(dishY)}`,
    `L${n(x0 + 0.3)},${n(t + 1.6)}`,
    `Q${n(x0 + 0.4)},${n(t)} ${n(cx - dip)},${n(t + 0.2)}`,
    `Q${n(cx)},${n(t + p.dipDepth + 0.4)} ${n(cx + dip)},${n(t + 0.2)}`,
    `Q${n(x1 - 0.4)},${n(t)} ${n(x1 - 0.3)},${n(t + 1.6)}`,
    `L${n(x1)},${n(dishY)}`,
    `Q${n(cx)},${n(dishY + 2.6)} ${n(x0)},${n(dishY)} Z`,
  ].join(' ');

  const drip = shape === 'stub'
    // One fat drip from the right shoulder, down the side and over the dish lip.
    ? `M${n(x1 - 3.4)},${n(t + 1)} Q${n(x1 + 1.5)},${n(t + 0.3)} ${n(x1 + 1.3)},${n(t + 3.5)} L${n(x1 + 1.3)},${n(dishY + 3.4)} Q${n(x1 + 1.3)},${n(dishY + 6.2)} ${n(x1 - 0.6)},${n(dishY + 6.2)} Q${n(x1 - 2.5)},${n(dishY + 6.2)} ${n(x1 - 2.4)},${n(dishY + 3.4)} L${n(x1 - 2.4)},${n(t + 3.2)} Z`
    // A short drip just under the taper's melted rim.
    : `M${n(x1 - 2.8)},${n(t + 0.8)} Q${n(x1 + 1.1)},${n(t + 0.4)} ${n(x1 + 0.9)},${n(t + 3)} L${n(x1 + 0.9)},${n(t + 5.8)} Q${n(x1 + 0.9)},${n(t + 7.8)} ${n(x1 - 0.4)},${n(t + 7.8)} Q${n(x1 - 1.7)},${n(t + 7.8)} ${n(x1 - 1.6)},${n(t + 5.8)} L${n(x1 - 1.6)},${n(t + 2.6)} Z`;

  const flameBottom = t + 0.3;
  const fx = cx + 0.2;
  const flameTipY = flameBottom - p.flameHeight;
  const glowRy = p.flameHeight * 0.62;

  return {
    variant: shape,
    variantTier: normalizeGearVariant(variant),
    centerX: cx,
    dishY,
    waxTopY,
    flameTipY,
    dishBack: `M${n(cx - dr)},${n(dishY)} A${n(dr)},2.8 0 1,1 ${n(cx + dr)},${n(dishY)} A${n(dr)},2.8 0 1,1 ${n(cx - dr)},${n(dishY)} Z`,
    dishFront: `M${n(cx - dr)},${n(dishY)} Q${n(cx)},${n(dishY + 9.4)} ${n(cx + dr)},${n(dishY)} A${n(dr)},2.8 0 0,1 ${n(cx - dr)},${n(dishY)} Z`,
    dishShine: `M${n(cx - dr + 2.2)},${n(dishY - 0.9)} Q${n(cx - dr * 0.62)},${n(dishY - 2.3)} ${n(x0 - 1)},${n(dishY - 2.6)}`,
    pool: shape === 'stub'
      ? `M${n(x0 - 3)},${n(dishY + 0.4)} Q${n(x0 - 3)},${n(dishY - 1.6)} ${n(cx)},${n(dishY - 1.6)} Q${n(x1 + 3)},${n(dishY - 1.6)} ${n(x1 + 3)},${n(dishY + 0.4)} Q${n(cx)},${n(dishY + 3.4)} ${n(x0 - 3)},${n(dishY + 0.4)} Z`
      : '',
    wax,
    waxRim: `M${n(cx - dip)},${n(t + 0.3)} Q${n(cx)},${n(t + p.dipDepth + 0.6)} ${n(cx + dip)},${n(t + 0.3)}`,
    waxHighlight: `M${n(x0 + 1.7)},${n(t + 3)} L${n(x0 + 1.5)},${n(dishY - 1.4)}`,
    waxShade: `M${n(x1 - 1.5)},${n(t + 2.8)} L${n(x1 - 1.3)},${n(dishY - 0.6)}`,
    drip,
    flame: teardrop(fx, flameBottom, p.flameHalfWidth, p.flameHeight, 1.1),
    flameCore: teardrop(fx - 0.2, flameBottom - 0.6, p.flameHalfWidth * 0.48, p.flameHeight * 0.56, 0.5),
    wick: `M${n(fx - 0.1)},${n(t + 1)} Q${n(fx + 0.1)},${n(t - 0.6)} ${n(fx + 0.6)},${n(t - 2.4)}`,
    glow: {
      cx: Number((fx - 0.3).toFixed(2)),
      cy: Number((flameTipY - 0.6 + glowRy).toFixed(2)),
      rx: Number((p.flameHalfWidth * 2.4).toFixed(2)),
      ry: Number(glowRy.toFixed(2)),
    },
  };
}

/** Mix a hex colour toward warm white (0 = unchanged, 1 = white). */
function lighten(hex: string, t: number): string {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const value = parseInt(h, 16);
  if (h.length !== 6 || Number.isNaN(value)) return hex;
  const mixChannel = (channel: number, target: number) => Math.round(channel + (target - channel) * t);
  const r = mixChannel((value >> 16) & 0xff, 0xff);
  const g = mixChannel((value >> 8) & 0xff, 0xfd);
  const b = mixChannel(value & 0xff, 0xf0);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

/**
 * Render the candle head layer. `semanticAttrs` carries the descriptor's
 * data-equipment-* markers, like every other resolved gear family.
 */
export function renderCandleHead(
  waxColor: string,
  flameColor: string,
  variant: number,
  context: GearFitContext,
  semanticAttrs = '',
): string {
  const g = candleRecipe(bodyRigForGear(context), variant);
  const outline = GEAR_COLORS.outline;
  const attrs = `${semanticAttrs} data-gear-recipe="candle" data-gear-shape="${context.shape}" data-gear-fit-mode="free" data-gear-variant-tier="${g.variantTier}" data-gear-candle="${g.variant}"`.trim();
  const stroke = (d: string, color: string, width: number, opacity = 1) =>
    `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" stroke-linecap="round" opacity="${opacity}" ${NON_SCALING_STROKE} />`;
  const fill = (d: string, color: string, width: number, part = '') =>
    `<path ${part} d="${d}" fill="${color}" stroke="${outline}" stroke-width="${width}" stroke-linejoin="round" ${NON_SCALING_STROKE} />`;
  const drip = fill(g.drip, waxColor, 1.1, 'data-gear-candle-part="drip"');
  const { cx, cy, rx, ry } = g.glow;
  // Keyed by colour alone, so two inline ghosts sharing an id share identical stops.
  const glowId = `h2kCandleGlow${flameColor.replace('#', '').toLowerCase()}`;

  return `<g ${attrs}>`
    + `<defs><radialGradient id="${glowId}"><stop offset="0" stop-color="${flameColor}" stop-opacity="0.55" /><stop offset="0.55" stop-color="${flameColor}" stop-opacity="0.2" /><stop offset="1" stop-color="${flameColor}" stop-opacity="0" /></radialGradient></defs>`
    + `<ellipse data-gear-candle-part="glow" cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="url(#${glowId})" />`
    + fill(g.dishBack, HOLDER.metal, 1.2, 'data-gear-candle-part="dish"')
    + stroke(g.dishShine, HOLDER.shine, 1.1, 0.85)
    + (g.pool ? fill(g.pool, waxColor, 1, 'data-gear-candle-part="pool"') : '')
    + fill(g.wax, waxColor, 1.3, 'data-gear-candle-part="wax"')
    + stroke(g.waxShade, darken(waxColor, 0.78), g.variant === 'stub' ? 2.6 : 1.7, 0.55)
    + stroke(g.waxHighlight, '#ffffff', g.variant === 'stub' ? 2 : 1.3, 0.6)
    + stroke(g.waxRim, darken(waxColor, 0.72), 0.9, 0.8)
    + (g.variant === 'taper' ? drip : '')
    + fill(g.dishFront, HOLDER.shade, 1.2, 'data-gear-candle-part="dish-rim"')
    + (g.variant === 'stub' ? drip : '')
    + `<path data-gear-candle-part="flame" d="${g.flame}" fill="${flameColor}" stroke="${darken(flameColor, 0.6)}" stroke-width="1" stroke-linejoin="round" ${NON_SCALING_STROKE} />`
    + `<path data-gear-candle-part="flame-core" d="${g.flameCore}" fill="${lighten(flameColor, 0.66)}" />`
    + stroke(g.wick, WICK_COLOR, 1.5)
    + '</g>';
}
